// src/components/StatCard.tsx
import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Paper } from "@mui/material";
import { keyframes } from "@mui/system";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

type Props = {
    title: string;
    value: number;
    icon?: React.ReactNode;
    gradient?: string;
    delay?: number;
};

function useCountUp(target: number, duration = 1200) {
    const [count, setCount] = useState(0);
    const startRef = useRef<number | null>(null);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        startRef.current = null;
        const step = (ts: number) => {
            if (!startRef.current) startRef.current = ts;
            const progress = Math.min((ts - startRef.current) / duration, 1);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) rafRef.current = requestAnimationFrame(step);
        };
        rafRef.current = requestAnimationFrame(step);
        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [target, duration]);

    return count;
}

export default function StatCard({ title, value, icon, gradient, delay = 0 }: Props): React.ReactElement {
    const displayValue = useCountUp(value);

    const bg = gradient ?? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

    return (
        <Paper
            elevation={0}
            sx={{
                width: "100%",
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "rgba(0,0,0,0.07)",
                animation: `${fadeUp} 0.5s ease-out ${delay}ms both`,
                transition: "box-shadow 0.25s ease, transform 0.25s ease",
                "&:hover": {
                    boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
                    transform: "translateY(-3px)",
                },
                background: "#fff",
            }}
        >
            {/* Coloured top strip */}
            <Box sx={{ height: 4, background: bg }} />

            <Box sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
                {/* Icon bubble */}
                {icon && (
                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 2.5,
                            background: bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            flexShrink: 0,
                            fontSize: 24,
                            boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
                            "& svg": { fontSize: 26 },
                        }}
                    >
                        {icon}
                    </Box>
                )}

                <Box>
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{ color: "text.secondary", letterSpacing: 0.5, textTransform: "uppercase", fontSize: "0.7rem" }}
                    >
                        {title}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.1, mt: 0.3, letterSpacing: -1 }}>
                        {displayValue.toLocaleString()}
                    </Typography>
                </Box>
            </Box>
        </Paper>
    );
}
